import { useEffect, useRef, useState } from 'react'
import { Pause, Play } from 'lucide-react'
import { selectDriver } from './driver'
import { useAutoplayProgress } from './useAutoplayProgress'
import { sampleTranscript, derivedTasks } from './fixtures'
import TranscriptPane from './TranscriptPane'
import TaskCard from './TaskCard'
import BoardPane from './BoardPane'
import StaticFallback from './StaticFallback'

const SUMMARY =
  'An animation that turns a raw meeting transcript into Trello cards: action items are detected, then lifted out as cards with an assignee, due date and priority.'

/**
 * Both media queries this section cares about, through one hook.
 *
 * Deliberately not Motion's `useReducedMotion`: that caches its result in a
 * module-scoped singleton on first call, so it neither responds to a user
 * changing the setting at runtime nor re-reads `matchMedia` per component.
 * Reading the query directly costs nothing and behaves correctly.
 *
 * Initial state is read lazily rather than defaulting to false, so a
 * reduced-motion user never sees a frame of the animated canvas before the
 * effect swaps in the static fallback. This app is a client-only SPA, so
 * touching `window` during render is safe.
 */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  )

  useEffect(() => {
    const mq = window.matchMedia(query)
    const update = () => setMatches(mq.matches)
    update()
    mq.addEventListener('change', update)
    return () => mq.removeEventListener('change', update)
  }, [query])

  return matches
}

function AnimatedCanvas() {
  const containerRef = useRef<HTMLDivElement>(null)
  const { progress, isPlaying, togglePlaying } = useAutoplayProgress(containerRef)

  return (
    <div ref={containerRef} className="relative py-8">
      <div
        aria-hidden
        className="grid w-full gap-8 md:grid-cols-[1.05fr_0.95fr]"
      >
        <div className="max-h-[62vh] overflow-hidden rounded-xl border border-white/[0.06] bg-surface-overlay p-6">
          <TranscriptPane progress={progress} lines={sampleTranscript} />
        </div>
        <div className="self-start">
          <BoardPane progress={progress}>
            {derivedTasks.map((task, i) => (
              <TaskCard
                key={task.id}
                task={task}
                index={i}
                count={derivedTasks.length}
                progress={progress}
              />
            ))}
          </BoardPane>
        </div>
      </div>

      {/* Outside the aria-hidden wrapper: the canvas is decorative, but the
          control that stops it is real interface. The loop runs indefinitely,
          so WCAG 2.2.2 requires this to exist and to be reachable. */}
      <div className="mt-10 flex justify-center">
        <button
          type="button"
          onClick={togglePlaying}
          className="inline-flex items-center gap-2 rounded-full border border-white/10 px-3.5 py-2 text-caption text-white/50 transition-colors hover:border-white/20 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400"
        >
          {isPlaying ? (
            <Pause className="h-3.5 w-3.5" aria-hidden />
          ) : (
            <Play className="h-3.5 w-3.5" aria-hidden />
          )}
          {isPlaying ? 'Pause' : 'Play'}
        </button>
      </div>
    </div>
  )
}

export default function TransformSection() {
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
  const kind = selectDriver({ prefersReducedMotion })

  return (
    <section id="how-it-works-demo" className="mx-auto max-w-6xl px-4">
      <p className="sr-only">{SUMMARY}</p>
      {kind === 'static' ? <StaticFallback /> : <AnimatedCanvas />}
    </section>
  )
}
